import { onBeforeUnmount, ref, type Ref } from 'vue';
import type { CardPaymentSession } from '@/schemas/cardPaymentSession';
import { loadFlexLibrary, type FlexField, type FlexMicroform, type FlexTokenOptions } from '@/lib/flex';
import { microformStyles } from '@/theme';

interface MicroformContainers {
  number: Ref<HTMLElement | null>;
  securityCode: Ref<HTMLElement | null>;
}

function loadField(field: FlexField, container: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    field.on('load', () => resolve());
    // Pass the element, not a selector: the containers live in our shadow root.
    field.load(container);
  });
}

export function useMicroform(containers: MicroformContainers) {
  const isLoaded = ref(false);

  let microform: FlexMicroform | null = null;
  let fields: FlexField[] = [];
  let isDisposed = false;

  async function init(session: CardPaymentSession): Promise<void> {
    const Flex = await loadFlexLibrary(session.clientLibrary, session.clientLibraryIntegrity);
    if (isDisposed || !containers.number.value || !containers.securityCode.value) {
      return;
    }

    microform = new Flex(session.captureContext).microform({ styles: microformStyles });

    const number = microform.createField('number', { placeholder: '1234 1234 1234 1234' });
    const securityCode = microform.createField('securityCode', { placeholder: '•••' });
    fields = [number, securityCode];

    await Promise.all([
      loadField(number, containers.number.value),
      loadField(securityCode, containers.securityCode.value),
    ]);

    isLoaded.value = !isDisposed;
  }

  function createToken(options: FlexTokenOptions): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!microform) {
        reject(new Error('Payment form is not ready yet.'));

        return;
      }

      microform.createToken(options, (error, token) => (error ? reject(new Error(error.message)) : resolve(token)));
    });
  }

  onBeforeUnmount(() => {
    isDisposed = true;
    fields.forEach((field) => field.unload());
    fields = [];
    microform = null;
  });

  return { isLoaded, init, createToken };
}
